import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CobuStack, CobuText } from '@cobu/ui';
import { OfferDetails } from './OfferDetails';
import { YourResults } from './YourResults';
import { Strings } from '../../constants/Strings';
import type { ReferralOffer } from '@cobu/types/perk';

interface ReferralTabsProps {
  referralOffer: ReferralOffer;
}

interface Tab {
  key: string;
  title: string;
}

const referralTabsStrings = Strings.get().referral.tabs;

export const ReferralTabs: React.FC<ReferralTabsProps> = ({ referralOffer }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  
  // Initialize tab index based on URL parameter
  const getInitialTabIndex = () => {
    if (tabParam === 'yourResults') {
      return 1; // Your Results tab
    }
    return 0; // Offer Details tab (default)
  };
  
  const [activeTabIndex, setActiveTabIndex] = useState(getInitialTabIndex());
  
  // Update tab when URL parameter changes
  useEffect(() => {
    if (tabParam === 'yourResults') {
      setActiveTabIndex(1);
    } else if (tabParam === null || tabParam === 'offerDetails') {
      setActiveTabIndex(0);
    }
  }, [tabParam]);
  
  // Handle tab change and update URL
  const handleTabChange = (index: number) => {
    setActiveTabIndex(index);
    const newParams = new URLSearchParams(searchParams);
    if (index === 1) {
      newParams.set('tab', 'yourResults');
    } else {
      newParams.delete('tab'); // Remove tab param for default tab
    }
    setSearchParams(newParams, { replace: true });
  };
  
  const tabs: Tab[] = [
    { key: 'first', title: referralTabsStrings.offerDetails },
    { key: 'second', title: referralTabsStrings.yourResults },
  ];

  const renderTabContent = () => {
    switch (activeTabIndex) {
      case 1:
        return <YourResults />;
      default:
        return <OfferDetails referralOffer={referralOffer} />;
    }
  };

  const renderTabBarItem = (tab: Tab, index: number) => {
    const isActive = index === activeTabIndex;
    
    return (
      <CobuStack
        key={tab.key}
        onPress={() => handleTabChange(index)}
        role="button"
        tabIndex={0}
        br={"$24"}
        f={1}
        p={'$2'}
        minH={"$10"}
        h="auto"
        ai="center"
        jc="center"
        bg={isActive ? "$cobuDarkBlue" : "$white"}
        style={{ WebkitTapHighlightColor: 'transparent' as any, cursor: 'pointer' }}
      >
        <CobuText
          variant="body"
          fontWeight="semibold"
          ta="center"
          color={isActive ? "$white" : "$cobuDarkBlue"}
        >
          {tab.title}
        </CobuText>
      </CobuStack>
    );
  };

  return (
    <CobuStack f={1}>
      {/* Tab Bar */}
      <CobuStack 
        layout="horizontal" 
        jc="space-between" 
        mx="$4"
        mt="$3"
      >
        {tabs.map((tab, index) => renderTabBarItem(tab, index))}
      </CobuStack>
      
      {/* Tab Content */}
      <CobuStack f={1}>
        {renderTabContent()}
      </CobuStack>
    </CobuStack>
  );
}; 